import { signInWithEmailAndPassword, signOut, type User } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, query, setDoc, where } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

export function mapEmailAuthError(err: unknown): string {
  const code = (err as { code?: string })?.code || '';
  if (code === 'auth/invalid-email') return 'Please enter a valid email address.';
  if (code === 'auth/user-disabled') return 'This account has been disabled. Contact your administrator.';
  if (code === 'auth/user-not-found' || code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
    return 'Incorrect email or password.';
  }
  if (code === 'auth/too-many-requests') {
    return 'Too many attempts. Please wait a few minutes and try again.';
  }
  if (code === 'auth/network-request-failed') {
    return 'Sign-in failed due to a network error. Check your connection and try again.';
  }
  if (code === 'auth/operation-not-allowed') {
    return 'Email and password sign-in is disabled in Firebase. Enable it under Authentication → Sign-in method.';
  }
  return `Sign-in failed. (${code || 'unknown'})`;
}

/** Email/password sign-in — same registration gate as Google sign-in. */
export async function finishEmailSignIn(user: User): Promise<{ ok: true } | { ok: false; error: string }> {
  const userRef = doc(db, 'users', user.uid);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    if (userSnap.data()?.isActive === false) {
      await signOut(auth);
      return { ok: false, error: 'This account has been disabled. Contact your administrator.' };
    }
    await setDoc(
      userRef,
      {
        lastLoginAt: new Date(),
        loginMethod: 'password',
      },
      { merge: true },
    );
    return { ok: true };
  }

  if (user.email) {
    const email = user.email.trim().toLowerCase();
    const usersQuery = query(collection(db, 'users'), where('email', '==', email));
    const emailSnap = await getDocs(usersQuery);

    if (!emailSnap.empty) {
      const existingData = emailSnap.docs[0].data();
      if (existingData.isActive === false) {
        await signOut(auth);
        return { ok: false, error: 'This account has been disabled. Contact your administrator.' };
      }
      await setDoc(userRef, {
        ...existingData,
        uid: user.uid,
        lastLoginAt: new Date(),
        loginMethod: 'password',
      });
      return { ok: true };
    }

    const businessEmailMatches = await Promise.all([
      getDocs(query(collection(db, 'restaurants'), where('email', '==', email))),
      getDocs(query(collection(db, 'restaurants'), where('operatorEmail', '==', email))),
      getDocs(query(collection(db, 'restaurants'), where('accountantEmail', '==', email))),
    ]);
    if (businessEmailMatches.some((snapshot) => !snapshot.empty)) {
      return { ok: true };
    }
  }

  await signOut(auth);
  return { ok: false, error: 'This account is not registered. Please contact your administrator.' };
}

export async function startEmailSignIn(
  email: string,
  password: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const normalizedEmail = email.trim().toLowerCase();
  const result = await signInWithEmailAndPassword(auth, normalizedEmail, password);
  return finishEmailSignIn(result.user);
}
